import React, { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { api } from '../../../lib/api';
import TaskRow from '../../../components/crm/TaskRow';
import EmptyState from '../../../components/crm/EmptyState';
import TaskFormDialog from '../../../components/admin/TaskFormDialog';

// Per-account Tasks tab. Uses the same rich TaskFormDialog as the dashboard
// "+ New Task" entrypoint so creators can pick an assignee, due date,
// priority, status, related entity, and reminder cadence — instead of the
// minimal inline form that lived here previously (title + priority only,
// no way to assign).
//
// The dialog is pre-pinned to this account via the new `accountId` prop on
// TaskFormDialog so the POST writes the right account_id without any user
// effort.
export default function TasksTab({ account }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/admin/accounts/${account.id}/tasks?include_done=true`);
      setTasks(res.tasks || []);
    } finally {
      setLoading(false);
    }
  }, [account.id]);

  useEffect(() => { load(); }, [load]);

  const onComplete = async (task) => {
    const res = await api.post(`/api/admin/tasks/${task.id}/complete`);
    setTasks((curr) => curr.map((t) => (t.id === task.id ? res.task : t)));
  };

  const onDelete = async (task) => {
    if (!window.confirm(`Delete task "${task.title}"?`)) return;
    await api.delete(`/api/admin/tasks/${task.id}`);
    setTasks((curr) => curr.filter((t) => t.id !== task.id));
  };

  if (loading && tasks.length === 0) {
    return <div className="animate-pulse h-12 rounded-lg bg-white/[0.03]" />;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="text-[12px] text-gray-500">
          {tasks.length} task{tasks.length === 1 ? '' : 's'}
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] text-gray-900 bg-[#B0FC38]/80 hover:bg-[#B0FC38]"
          data-testid="account-tasks-new"
        >
          <Plus className="w-3 h-3" /> New Task
        </button>
      </div>

      {tasks.length === 0 ? (
        <EmptyState
          title="No tasks for this account yet."
          action={
            <button
              onClick={() => setShowNew(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] text-gray-900 bg-[#B0FC38]/80 hover:bg-[#B0FC38]"
            >
              <Plus className="w-3 h-3" /> Add task
            </button>
          }
        />
      ) : (
        <div className="space-y-2">
          {tasks.map((t) => (
            <TaskRow key={t.id} task={t} onComplete={onComplete} onDelete={onDelete} />
          ))}
        </div>
      )}

      <TaskFormDialog
        open={showNew}
        accountId={account.id}
        onClose={() => setShowNew(false)}
        onSaved={(saved) => {
          if (saved) setTasks((curr) => [saved, ...curr]);
          setShowNew(false);
        }}
      />
    </div>
  );
}
